"use client";

import type { CartItem } from "./CartContext";
import { faMinus, faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { useTranslations } from "next-intl";
import { addToCartAction, setCartQtyAction } from "@/app/[locale]/(shop)/cart/actions";
import { Icon } from "@/components/ui/Icon";
import { MAX_LINE_QTY } from "@/lib/commerce/cart-qty";
import { cartKey } from "./cart-keys";
import { useCart } from "./CartContext";

/**
 * ## ⚠️ A real `<form action>`, so it works with no JavaScript (TRY-128)
 *
 * It was a `<button onClick={add}>`, which is inert without JS — and the cart it
 * wrote lived in `localStorage`, which no server could read. The form posts the
 * server action directly (NOT through `useActionState`, which is what withholds the
 * hidden `$ACTION_ID_*` input and makes a no-JS POST 500 — see FORMS.md §11).
 *
 * With JS the `onSubmit` below takes over so the optimistic update and the
 * "added to cart" flash still happen; `preventDefault` is what keeps the two from
 * both firing. Without JS the browser posts the form, the action writes the cookie
 * and redirects back to `back`, and the page re-renders with the new cart.
 *
 * Once the line is in the cart the button IS the line: it becomes a quantity
 * stepper in place, so the card says the product is in the cart and how many,
 * and a phone shopper (who gets no cart panel on add) can adjust it without
 * leaving the grid. The last step down is the bin, because `qty: 0` removes the
 * line. Only for a product with nothing to choose: a configured product has
 * many possible lines, and its card links to the product page instead.
 */
export function AddToCartButton({
	item,
	disabled,
	back,
	maxQty = 99,
}: {
	item: Omit<CartItem, "qty">;
	disabled?: boolean;
	/**
	 * Where a no-JS submit returns to — the current path. Omitted means the action
	 * re-renders in place, which is right for a JS submit and leaves a no-JS one on
	 * whatever the action returns.
	 */
	back?: string;
	/** The stepper's ceiling: the stock on hand for a tracked product. */
	maxQty?: number;
}) {
	const t = useTranslations("shop");
	const { add, setQty, items, viewOnly } = useCart();

	if (viewOnly) return null;

	const key = cartKey(item.productId, item.variantId, item.addons, item.note);
	const qty =
		items.find((i) => cartKey(i.productId, i.variantId, i.addons, i.note) === key)?.qty ?? 0;
	const backInput = back && <input type="hidden" name="back" value={back} />;
	// Mounted in both states so the switch is announced: a region that mounts
	// with its text is not, and below `md` no panel takes focus to say it.
	const live = (
		<span className="sr-only" aria-live="polite">
			{qty > 0 ? t("inCartCount", { n: qty }) : ""}
		</span>
	);

	// Kept when the product sells out with a line in the cart: `+` is already
	// off at the stock cap, and the shopper can still step down or remove it.
	if (qty > 0) {
		const step = (next: number, label: string, icon: typeof faPlus, off = false) => (
			<form
				action={setCartQtyAction}
				onSubmit={(e) => {
					e.preventDefault();
					setQty(key, next);
				}}
			>
				<input type="hidden" name="key" value={key} />
				<input type="hidden" name="qty" value={next} />
				{backInput}
				<button
					type="submit"
					aria-label={label}
					disabled={off}
					className="grid h-full w-9 place-items-center sm:w-11 transition hover:bg-black/10 disabled:opacity-40"
				>
					<Icon icon={icon} size={12} />
				</button>
			</form>
		);
		return (
			<>
				<div
					role="group"
					aria-label={t("quantity")}
					// The primary fill the add button had, so the card keeps its weight and
					// the change of state reads as the same control answering.
					className="flex h-[2.45rem] w-full items-stretch justify-between overflow-hidden rounded-[var(--radius-btn,var(--radius-md))] text-sm font-semibold"
					style={{ background: "var(--gold)", color: "var(--gold-foreground)" }}
				>
					{step(qty - 1, qty <= 1 ? t("remove") : t("decreaseQty"), qty <= 1 ? faTrash : faMinus)}
					<span className="grid min-w-0 flex-1 place-items-center whitespace-nowrap tabular-nums">
						{t("inCartCount", { n: qty })}
					</span>
					{step(qty + 1, t("increaseQty"), faPlus, qty >= Math.min(maxQty, MAX_LINE_QTY))}
				</div>
				{live}
			</>
		);
	}

	return (
		<>
			<form
				action={addToCartAction}
				onSubmit={(e) => {
					// JS present: keep the optimistic path, which also carries the flash.
					e.preventDefault();
					add(item);
				}}
			>
				<input type="hidden" name="productId" value={item.productId} />
				{item.variantId && <input type="hidden" name="variantId" value={item.variantId} />}
				{item.addons && item.addons.length > 0 && (
					<input
						type="hidden"
						name="addons"
						value={JSON.stringify(item.addons.map((a) => ({ gi: a.gi, ci: a.ci })))}
					/>
				)}
				{item.note && <input type="hidden" name="note" value={item.note} />}
				{backInput}
				<button type="submit" disabled={disabled} className="btn btn-primary w-full text-sm">
					{disabled ? t("outOfStock") : t("addToCart")}
				</button>
			</form>
			{live}
		</>
	);
}
